'use client';

import { useMemo } from 'react';
import { useApolloClient } from '@frameio/graphql-client';
import {
  type AssetType,
  type AssetTypeMap,
  useFocusedAsset,
  useSelectedAssets,
  useSelectionAssetTypeMap,
} from '@frameio/selection';
import { useContextMenuTarget } from '@frameio/shared/contexts';
import {
  AssetForContextMenu,
  type AssetForContextMenuFragment,
} from '../../shared/gql/generated/AssetForContextMenu';

type AssetTypeCounts = Map<AssetType, number>;
export type SelectionType = 'all-folders' | 'all-files' | 'mixed';

export type SelectionForOperationsReturn = {
  /** map of asset typename to a count of how many assets match that type */
  assetTypeCounts: AssetTypeCounts;
  /** map of asset id to its __typename for this selection */
  assetTypeMap: Record<string, AssetType | undefined>;
  /** the AssetForContextMenu fragment of the original focused asset */
  focusedAsset: AssetForContextMenuFragment | null;
  /** the id of the focused asset */
  focusedAssetId: string | null;
  /** array of selected asset ids (version ids transformed into stack ids) */
  selectedIds: string[];
  /** description of whether the selection includes all folders, files or a mix */
  selectionType: SelectionType;
  /** whether we would need to update the global focused asset state */
  shouldFocusAsset: boolean;
};

/**
 * The useSelectionForOperations hook provides useful information for running
 * asset operations based on the current selection. The main thing it attempts to
 * simplify is breaking down whether the user means to operate on the selection
 * or on the asset that was right-clicked (aka focused).
 */
export function useSelectionForOperations(
  ignoreFocusedAsset: boolean
): SelectionForOperationsReturn {
  const { cache } = useApolloClient();
  const selection = useSelectedAssets();
  const contextMenuTarget = useContextMenuTarget<string | null | undefined>();
  const focusedAssetId = useFocusedAsset() ?? contextMenuTarget;
  const assetTypeMap = useSelectionAssetTypeMap();

  const groupedBaseSelection = useMemo(() => {
    const map: AssetTypeCounts = new Map();

    selection.forEach((id) => {
      const assetType = assetTypeMap[id];

      if (!assetType) return;

      map.set(assetType, (map.get(assetType) ?? 0) + 1);
    });

    return map;
  }, [assetTypeMap, selection]);

  return useMemo(() => {
    const nextSelection = selection.values().next().value;

    let focusId: string | null = nextSelection ?? null;
    let focusIsInSelection = false;

    if (!ignoreFocusedAsset) {
      if (focusedAssetId && selection.has(focusedAssetId)) {
        // set focusId to the context menu target if it's in the current selection.
        // This is useful for operations that need to know what item the menu was opened on,
        // even if that item is somewhere in the middle of the selection.
        focusId = focusedAssetId;
        focusIsInSelection = true;
      } else {
        // if we're not ignoring the focused asset, and the context menu target is not
        // in the current selection, then we override the target
        focusId = focusedAssetId ?? null;
      }
    }

    const focusedAsset = focusId
      ? cache.readFragment({
          fragment: AssetForContextMenu,
          fragmentName: 'AssetForContextMenu',
          id: cache.identify({ __typename: 'Asset', id: focusId }),
        })
      : null;
    const shouldFocusAsset =
      !ignoreFocusedAsset && focusId !== null && !focusIsInSelection;

    let selectedIds: string[];

    if (shouldFocusAsset && focusId !== null) {
      selectedIds = [focusId];
    } else {
      selectedIds = Array.from(selection);
    }

    let assetTypeCounts: AssetTypeCounts = groupedBaseSelection;

    // if there's a focused asset, we have to create the grouping for the
    // focused asset in order to provide a consistent API
    if (
      shouldFocusAsset &&
      selectedIds.length <= 1 &&
      focusedAsset?.__typename
    ) {
      assetTypeCounts = new Map();
      assetTypeCounts.set(focusedAsset.__typename, 1);
    }

    let selectionType: SelectionType = 'mixed';

    if (!assetTypeCounts.has('FolderAsset')) {
      selectionType = 'all-files';
    } else if (
      assetTypeCounts.size === 1 &&
      assetTypeCounts.has('FolderAsset')
    ) {
      selectionType = 'all-folders';
    }

    // the assetTypeMap from the SelectionContext only collects typenames for
    // assets that have been selected. this block handles the case where an asset
    // is focused but NOT selected, and provides the typename for the focused asset
    let reducedAssetTypeMap;

    if (shouldFocusAsset && focusedAsset) {
      reducedAssetTypeMap = {
        [focusedAsset.id]: focusedAsset.__typename,
      };
    } else {
      reducedAssetTypeMap = selectedIds.reduce((acc, id) => {
        acc[id] = assetTypeMap[id] ?? undefined;

        return acc;
      }, {} as AssetTypeMap);
    }

    return {
      assetTypeCounts,
      assetTypeMap: reducedAssetTypeMap,
      focusedAsset,
      focusedAssetId: focusId,
      selectedIds,
      selectionType,
      shouldFocusAsset,
    };
  }, [
    assetTypeMap,
    cache,
    focusedAssetId,
    groupedBaseSelection,
    ignoreFocusedAsset,
    selection,
  ]);
}
